import { Schema } from "effect"
// Type-only import: underwriter.ts value-imports ExternalContactPref from here, so
// importing ClaimStatus as a VALUE would create a runtime import cycle. The type
// is elided at runtime, so this stays acyclic.
import type { ClaimStatus } from "./underwriter"

/**
 * How an underwriter profile accepts inbound contact from EXTERNAL brokers.
 *
 * A closed, engineering-owned security/access-policy domain — it governs who may
 * reach the underwriter, NOT a user-extensible taxonomy (see
 * agent_docs/standards/user-empowerment.md). Calls imply messages: there is
 * deliberately no "calls but not messages" state.
 */
export const ExternalContactPref = Schema.Literal("calls_and_messages", "messages_only", "none").annotations({
  identifier: "ExternalContactPref",
  title: "External Contact Preference",
  description: "Whether external brokers may message and/or call this underwriter profile",
})
export type ExternalContactPref = Schema.Schema.Type<typeof ExternalContactPref>

/** The default the moment a profile is CLAIMED — reachable by text, calls opt-in. */
export const DEFAULT_EXTERNAL_CONTACT_PREF: ExternalContactPref = "messages_only"

/**
 * May an external broker START a new message thread with this underwriter?
 *
 * Unclaimed profiles keep the existing lead-capture behaviour (a broker's message
 * creates a null-org thread that waits for a claim). Claimed profiles are gated by
 * their preference: only `none` blocks messaging.
 */
export const canMessageExternally = (u: {
  claimStatus: ClaimStatus
  externalContactPref: ExternalContactPref
}): boolean => (u.claimStatus !== "verified" ? true : u.externalContactPref !== "none")

/**
 * May an external broker CALL this underwriter (does the ring reach them)?
 *
 * Unclaimed = false (no one to answer — there is no servicer anyway). Claimed =
 * only when calls are explicitly enabled.
 */
export const canCallExternally = (u: {
  claimStatus: ClaimStatus
  externalContactPref: ExternalContactPref
}): boolean => u.claimStatus === "verified" && u.externalContactPref === "calls_and_messages"
